import { useNavigate } from "react-router-dom";
import {
  NotificationBellPopover,
  type NotificationBellItem,
} from "@a2z/ui";
import { useNotificationsContext } from "../NotificationsProvider";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import { getSellerPath } from "@/lib/routing/portal-urls";
import type { NotificationBellProps } from "../types/notification-bell.types";
import { intlLocale } from "@/lib/formatting/locale";
import { notificationCopy } from "@/shared/notifications/notification-copy";

function formatRelativeTime(date: Date, locale: string): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMinutes < 1) return locale === "ar" ? "الآن" : "Just now";
  if (diffMinutes < 60) {
    return locale === "ar" ? `منذ ${diffMinutes} د` : `${diffMinutes}m ago`;
  }
  if (diffHours < 24) {
    return locale === "ar" ? `منذ ${diffHours} س` : `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return locale === "ar" ? `منذ ${diffDays} ي` : `${diffDays}d ago`;
  }
  return date.toLocaleDateString(intlLocale(locale), {
    month: "short",
    day: "numeric",
  });
}

/** Seller-owned data/routing wrapper around the shared bell presentation. */
export function NotificationBell({
  locale,
  storeSlug,
  triggerClassName,
}: NotificationBellProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead, soundStatus, enableSound, muteSound } =
    useNotificationsContext();

  const items: NotificationBellItem[] = notifications.map((notification) => {
    const copy = notificationCopy(notification, t);
    return {
      id: notification.id,
      title: copy.title,
      body: copy.body,
      read: notification.read,
      timeLabel: notification.createdAt
        ? formatRelativeTime(notification.createdAt.toDate(), locale)
        : undefined,
    };
  });

  return (
    <NotificationBellPopover
      ariaLabel={t(keys.notifications.title)}
      title={t(keys.notifications.title)}
      markAllReadLabel={t(keys.notifications.markAllRead)}
      emptyLabel={t(keys.notifications.empty)}
      items={items}
      unreadCount={unreadCount}
      onMarkAllRead={markAllAsRead}
      onItemClick={(item) => {
        void markAsRead(item.id);
        const notification = notifications.find((entry) => entry.id === item.id);
        const orderId = notification?.data?.orderId;
        if (orderId) {
          navigate(
            `${getSellerPath(locale, storeSlug, "/orders")}?order=${orderId}`,
          );
        }
      }}
      notice={{
        title: t(keys.notifications.soundTitle),
        body: t(soundStatus === "ready" ? keys.notifications.soundReady : soundStatus === "muted" ? keys.notifications.soundMuted : soundStatus === "unsupported" ? keys.notifications.soundUnsupported : keys.notifications.soundLocked),
        actionLabel: soundStatus === "unsupported" ? undefined : t(soundStatus === "ready" ? keys.notifications.soundMute : keys.notifications.soundEnable),
        onAction: soundStatus === "ready" ? muteSound : enableSound,
      }}
      triggerClassName={triggerClassName}
    />
  );
}
